import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCodeStyling from 'qr-code-styling';
import { api } from '../utils/api';

export default function MarketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [market, setMarket] = useState(null);
  const [vendors, setVendors] = useState([]);
  const qrRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Public endpoint - no auth token needed
        const res = await api(`/api/markets/${id}`, {});
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setMarket({
            id: data.id,
            name: data.name,
            code: data.code,
            vendorLimit: data.vendorLimit,
            currentVendorCount: data.currentVendorCount,
            createdAt: data.createdAt
          });
          setVendors(data.vendors || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Generate QR codes for each vendor
  useEffect(() => {
    if (vendors.length === 0) return;

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      vendors.forEach((vendor) => {
        if (!vendor.id) return;

        const qrContainer = document.getElementById(`qr-${vendor.id}`);
        if (!qrContainer) return;

        // Skip if QR code already exists for this vendor
        if (qrRefs.current[vendor.id]) return;

        const vendorUrl = `${window.location.origin}/vendor/${vendor.id}`;
        
        const qrCode = new QRCodeStyling({
          width: 200,
          height: 200,
          type: 'svg',
          data: vendorUrl,
          dotsOptions: {
            color: '#1e293b',
            type: 'rounded',
          },
          backgroundOptions: {
            color: '#ffffff',
          },
          cornersSquareOptions: {
            color: '#1e293b',
            type: 'extra-rounded',
          },
          cornersDotOptions: {
            color: '#1e293b',
            type: 'dot',
          },
        });

        qrRefs.current[vendor.id] = qrCode;
        qrCode.append(qrContainer);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      // Cleanup QR codes
      Object.values(qrRefs.current).forEach((qrCode) => {
        if (qrCode) {
          try {
            // Clear the container if possible
            const container = qrCode._container;
            if (container) {
              container.innerHTML = '';
            }
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      });
      qrRefs.current = {};
    };
  }, [vendors]);

  const handleVendorClick = (vendorId) => {
    navigate(`/vendor/${vendorId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500 dark:text-slate-400">Loading garage sale details…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Market Details</h1>
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      </div>
    );
  }

  if (!market) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Market Details</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">
          Market not found.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Market Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-semibold">{market.name} Garage sale</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">
          {vendors.length} {vendors.length === 1 ? 'vendor' : 'vendors'} in this market
        </p>
      </div>

      {/* Vendors Grid */}
      {vendors.length === 0 ? (
        <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            No vendors in this market yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {vendors.map((vendor) => (
            <div
              key={vendor.id}
              onClick={() => handleVendorClick(vendor.id)}
              className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden cursor-pointer hover:border-indigo-500 dark:hover:border-indigo-400 hover:shadow-lg transition-all"
            >
              <div className="p-6 flex flex-col md:flex-row gap-6">
                {/* Vendor Info Section */}
                <div className="flex-1 flex flex-col gap-4">
                  {/* Store Name */}
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-semibold">
                      {vendor.storeName ? `${vendor.storeName} Garage sale` : 'Unnamed Store'}
                    </h2>
                    {vendor.isOwner && (
                      <span className="px-2 py-1 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 text-xs font-medium">
                        Owner
                      </span>
                    )}
                  </div>

                  {/* Location */}
                  {vendor.locationText && (
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Location</span>
                      <span className="text-lg">{vendor.locationText}</span>
                    </div>
                  )}

                  {/* Store Hours */}
                  {vendor.storeHours && (
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Hours</span>
                      <span className="text-lg whitespace-pre-line">{vendor.storeHours}</span>
                    </div>
                  )}

                  {/* Phone */}
                  {vendor.phone && (
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Phone</span>
                      <span className="text-lg">{vendor.phone}</span>
                    </div>
                  )}

                  {/* Click Indicator */}
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                      Click to view store →
                    </p>
                  </div>
                </div>

                {/* QR Code Section */}
                <div className="flex flex-col items-center gap-2">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Profile QR</span>
                  <div
                    id={`qr-${vendor.id}`}
                    className="flex justify-center bg-white p-2 rounded-xl border border-slate-200 dark:border-slate-700"
                    style={{ width: '200px', height: '200px', minHeight: '200px' }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
